import { useState } from "react";
import { Target, Heart, TrendingUp, Globe, Trophy, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import DemoModal from "@/components/DemoModal";

const MIC_IMAGE = "https://customer-assets.emergentagent.com/job_performance-hub-263/artifacts/8p26u7za_MIC.jpeg";

const values = [
  {
    icon: Target,
    title: "Precision First",
    desc: "Every feature is built around accuracy and reliability. We don't ship metrics that aren't meaningful.",
  },
  {
    icon: Heart,
    title: "Built for Coaches",
    desc: "We work closely with active coaches to ensure every feature solves real problems on real pitches.",
  },
  {
    icon: TrendingUp,
    title: "Continuous Improvement",
    desc: "Like the athletes we serve, we're always iterating, improving, and raising the standard.",
  },
  {
    icon: Globe,
    title: "Football for Everyone",
    desc: "We believe data-driven coaching shouldn't be limited to elite clubs. We're making it accessible to all.",
  },
];

const team = [
  { name: "Shreyas", role: "CEO & Co-Founder", bg: "bg-green-600" },
  { name: "Sushanth Keshav", role: "Co-Founder", bg: "bg-gray-800" },
];

const stats = [
  { value: "2025", label: "Founded" },
  { value: "200+", label: "Academies Served" },
  { value: "50K+", label: "Players Tracked" },
  { value: "12", label: "Countries" },
];

export default function AboutPage() {
  const [demoOpen, setDemoOpen] = useState(false);

  return (
    <div className="min-h-screen">
      <div className="bg-ink pt-32 pb-20" data-testid="about-page-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-4">
                Our Mission
              </p>
              <h1
                className="text-5xl md:text-6xl font-black text-white mb-6"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                Making Elite Coaching{" "}
                <span className="text-white/80">Accessible to All</span>
              </h1>
              <p className="text-gray-400 text-lg leading-relaxed">
                We started NextTackle because we believed every football academy deserves the same
                analytical tools used by professional clubs — without the million-pound price tag.
              </p>
            </div>
            <div className="relative">
              <img
                src={MIC_IMAGE}
                alt="NextTackle founders with Minerva FC at MIC Cup"
                className="rounded-sm w-full h-80 object-cover"
              />
              <div className="absolute inset-0 bg-black/20 rounded-sm" />
              <div className="absolute bottom-4 left-4 bg-green-600/90 backdrop-blur-sm px-3 py-1.5 rounded-sm">
                <p className="text-white text-xs font-bold uppercase tracking-wider">MIC Cup · Spain</p>
                <p className="text-green-200 text-xs">Minerva Football Club</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="py-24 bg-white" data-testid="about-story">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs uppercase tracking-widest text-forest font-semibold mb-3 text-center">
            Our Story
          </p>
          <h2
            className="text-4xl font-semibold text-ink tracking-tight mb-10 text-center"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            How NextTackle Was Born
          </h2>
          <div className="space-y-6 text-gray-600 leading-relaxed text-lg">
            <p>
              NextTackle was founded in 2025 by Shreyas and Sushanth Keshav — two passionate
              football enthusiasts who saw the same gap: academies were making crucial player
              development decisions based on memory, gut instinct, and handwritten notebooks.
            </p>
            <p>
              Professional clubs had access to sophisticated data tools. Grassroots and
              semi-professional academies had spreadsheets. That gap needed closing.
            </p>
            <p>
              We worked directly with football academies across Europe to understand exactly
              what coaches needed — not what we thought they needed, but what they actually
              used on a daily basis.
            </p>
            <p>
              The result is NextTackle: an analytics platform that feels as natural as picking
              up a clipboard, but gives you the insight of a full sports science department.
            </p>
          </div>
        </div>
      </div>

      <div className="py-16 bg-forest" data-testid="about-stats">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {stats.map((s) => (
              <div key={s.label}>
                <div
                  className="text-4xl font-black text-white mb-2"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  {s.value}
                </div>
                <div className="text-green-100 text-sm">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Minerva FC / MIC Cup Credibility */}
      <div className="bg-gray-950 overflow-hidden" data-testid="about-minerva">
        <div className="grid lg:grid-cols-2 min-h-[520px]">
          <div className="relative min-h-[320px] lg:min-h-0">
            <img
              src={MIC_IMAGE}
              alt="Minerva FC at the MIC Cup"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/30" />
          </div>
          <div className="px-8 py-16 lg:px-16 flex flex-col justify-center">
            <p className="text-xs uppercase tracking-[0.2em] text-white/50 font-semibold mb-4">
              Where It Started
            </p>
            <h2
              className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-6"
              style={{ fontFamily: "'Outfit', sans-serif" }}>
              On The Ground With <span className="text-white">Minerva FC</span>
            </h2>
            <p className="text-gray-400 leading-relaxed mb-5">
              Before a single line of code was written, Shreyas and Sushanth were pitch-side at the <strong className="text-white">MIC Cup in Spain</strong> — the world's most prestigious youth football tournament — working directly with <strong className="text-white">Minerva Football Club</strong>'s coaching and operations team.
            </p>
            <p className="text-gray-400 leading-relaxed mb-8">
              They observed the real friction points: manual attendance sheets, scattered session notes, no unified player history. The insight from that experience is baked into every feature of NextTackle.
            </p>
            <div className="flex flex-wrap gap-3">
              {[
                { icon: Trophy, label: "MIC Cup, Costa Brava" },
                { icon: MapPin, label: "Minerva Football Club" },
                { icon: Target, label: "Youth Development Focus" },
              ].map(({ icon: Icon, label }) => (
                <div key={label} className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4 py-2">
                  <Icon className="w-4 h-4 text-white/60 flex-shrink-0" />
                  <span className="text-gray-300 text-xs font-medium">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="py-24 bg-cream" data-testid="about-values">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs uppercase tracking-widest text-forest font-semibold mb-3 text-center">
            What We Stand For
          </p>
          <h2
            className="text-4xl font-semibold text-ink tracking-tight mb-12 text-center"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Our Values
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v) => (
              <div
                key={v.title}
                className="p-8 bg-white rounded-sm border border-gray-200 hover:border-green-300 hover:shadow-md transition-all"
              >
                <div className="w-12 h-12 bg-forest/10 rounded-xl flex items-center justify-center mb-5">
                  <v.icon className="w-6 h-6 text-forest" />
                </div>
                <h3
                  className="text-base font-semibold text-ink mb-2"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  {v.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="py-24 bg-white" data-testid="about-team">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs uppercase tracking-widest text-forest font-semibold mb-3 text-center">
            The People Behind NextTackle
          </p>
          <h2
            className="text-4xl font-semibold text-ink tracking-tight mb-12 text-center"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Meet the Team
          </h2>
          <div className="flex justify-center gap-12">
            {team.map((member) => (
              <div key={member.name} className="text-center group">
                <div
                  className={`w-24 h-24 ${member.bg} rounded-sm flex items-center justify-center text-white text-3xl font-black mx-auto mb-4 group-hover:scale-105 transition-transform`}
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  {member.name.charAt(0)}
                </div>
                <div className="text-sm font-bold text-gray-900">{member.name}</div>
                <div className="text-xs text-gray-500 mt-0.5">{member.role}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="py-20 bg-ink text-center">
        <div className="max-w-3xl mx-auto px-4">
          <h2
            className="text-4xl font-black text-white mb-4"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Ready to join the NextTackle family?
          </h2>
          <p className="text-gray-400 mb-8">
            Start your 3-month free trial and see why 200+ academies trust us with their player
            development.
          </p>
          <Button
            onClick={() => setDemoOpen(true)}
            className="bg-forest text-cream hover:bg-forest/90 rounded-full px-8 py-3 h-auto"
            data-testid="about-demo-btn"
          >
            Book a Demo
          </Button>
        </div>
      </div>

      <DemoModal open={demoOpen} onClose={() => setDemoOpen(false)} />
    </div>
  );
}
